import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe — Anchor Services" },
      { name: "description", content: "Manage your email preferences for Anchor Services property care updates." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Unsubscribe — Anchor Services" },
      { property: "og:description", content: "Manage your Anchor Services email preferences." },
    ],
    links: [{ rel: "canonical", href: "https://anchorsrq.com/unsubscribe" }],
  }),
  component: UnsubscribePage,
});

type State = "loading" | "valid" | "invalid" | "already" | "done" | "error";

function UnsubscribePage() {
  const [state, setState] = useState<State>("loading");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    setToken(t);
    if (!t) {
      setState("invalid");
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.valid) setState("valid");
        else if (d?.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      })
      .catch(() => setState("error"));
  }, []);

  const confirm = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const d = await res.json();
      if (d?.success) setState("done");
      else if (d?.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-6 py-20 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Email preferences</h1>
      {state === "loading" && <p className="mt-4 text-muted-foreground">Checking your link…</p>}
      {state === "valid" && (
        <>
          <p className="mt-4 text-muted-foreground">
            Confirm that you no longer want to receive emails from Anchor Services.
          </p>
          <Button className="mt-6" onClick={confirm} disabled={busy}>
            {busy ? "Unsubscribing…" : "Confirm unsubscribe"}
          </Button>
        </>
      )}
      {state === "already" && (
        <p className="mt-4 text-muted-foreground">You're already unsubscribed. No further emails will be sent.</p>
      )}
      {state === "done" && (
        <p className="mt-4 text-muted-foreground">You've been unsubscribed. Sorry to see you go!</p>
      )}
      {state === "invalid" && (
        <p className="mt-4 text-muted-foreground">This unsubscribe link is invalid or has expired.</p>
      )}
      {state === "error" && (
        <p className="mt-4 text-muted-foreground">Something went wrong. Please try again or email info@anchorsrq.com.</p>
      )}
    </main>
  );
}